import React, { useState } from 'react';
import { Box, Checkbox, FormControlLabel, Tab, Tabs, Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { toast } from 'react-toastify';
import { generateApiFailErrorMessage } from 'utils/helpers';
import { useQuery } from 'react-query';
import AIAssistantService, {
  GetFeedbackResponse,
  GetConversationResponse
} from 'services/hooks/ai-assistant-service';
import { AIAnalyticsType } from 'utils/constants';
import ChatCard from './ChatCard';
import ChatCardSkeleton from 'components/elements/Skeleton/ChatCardSkeleton';
import { formatDateToString } from 'utils/time';
import NoData from 'components/elements/Reports/NoData';

const LS_KEY_AI_ANALYTICS_TAB = 'ai.analytics.tab';

const AIAnalytics: React.FC = () => {
  const [startDate, setStartDate] = useState<Date>(
    new Date(new Date().setMonth(new Date().getMonth() - 1))
  );
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [excludeBline, setExcludeBline] = useState(false);
  const [activeTab, setActiveTab] = useState<AIAnalyticsType>(() => {
    const storedTab = localStorage.getItem(LS_KEY_AI_ANALYTICS_TAB) as AIAnalyticsType;
    return storedTab && Object.values(AIAnalyticsType).includes(storedTab)
      ? storedTab
      : AIAnalyticsType.Feedbacks;
  });

  // Format dates to 'YYYY-MM-DD' strings
  const start = formatDateToString(startDate);
  const end = formatDateToString(endDate);

  const fetchData = async () => {
    const params = {
      excludeBline,
      start,
      end
    };

    const endpoint =
      activeTab === AIAnalyticsType.Feedbacks
        ? AIAssistantService.getFeedbacks
        : AIAssistantService.getConversations;

    try {
      const data = await endpoint(params);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const { data, isLoading } = useQuery<GetFeedbackResponse[] | GetConversationResponse[], Error>(
    ['aiAnalyticsData', activeTab, start, end, excludeBline],
    fetchData,
    {
      onError: () => {
        toast.error(generateApiFailErrorMessage(activeTab));
      }
    }
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: AIAnalyticsType) => {
    setActiveTab(newValue);
    // Store the last selected analytics tab in localStorage
    localStorage.setItem(LS_KEY_AI_ANALYTICS_TAB, newValue);
  };

  const handleStartDateChange = (date: Date | null) => {
    if (!!date && date <= endDate) {
      setStartDate(date);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!!date && date >= startDate) {
      setEndDate(date);
    }
  };

  return (
    <Box style={{ marginTop: 40 }}>
      <Grid container justifyContent="space-between" style={{ paddingBottom: 20 }}>
        <Grid item>
          <h1 style={{ paddingBottom: 20 }}>AI Analytics</h1>
        </Grid>
      </Grid>

      {/* Filters */}
      <Grid container spacing={1} justifyContent="flex-start" alignItems="center">
        <Grid item>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={handleStartDateChange}
            slotProps={{
              textField: {
                size: 'small',
                InputProps: {
                  sx: { borderRadius: '10px', backgroundColor: 'white' }
                }
              }
            }}
            maxDate={endDate}
          />
        </Grid>
        <Grid item>
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={handleEndDateChange}
            slotProps={{
              textField: {
                size: 'small',
                InputProps: {
                  sx: { borderRadius: '10px', backgroundColor: 'white' }
                }
              }
            }}
            minDate={startDate}
          />
        </Grid>
        <Grid item>
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeBline}
                onChange={(e) => setExcludeBline(e.target.checked)}
              />
            }
            label="Exclude b-line users"
          />
        </Grid>
      </Grid>

      <Box sx={{ marginTop: 2 }}>
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Feedbacks" value={AIAnalyticsType.Feedbacks} />
          <Tab label="Conversations" value={AIAnalyticsType.Conversations} />
        </Tabs>

        {/* Main content */}
        {isLoading ? (
          <Box sx={{ marginTop: 2 }}>
            {[...Array(3)].map((_, index) => (
              <ChatCardSkeleton key={index} />
            ))}
          </Box>
        ) : (
          <Box sx={{ marginTop: 2 }}>
            {/* Feedbacks */}
            {activeTab === AIAnalyticsType.Feedbacks && data && (
              <>
                {(data as GetFeedbackResponse[]).length === 0 ? (
                  <NoData height={150} hint="Oops! No feedbacks found. Try tweaking the filters!" />
                ) : (
                  (data as GetFeedbackResponse[]).map((item) => (
                    <ChatCard key={item.uuid} data={item} isFeedback={true} />
                  ))
                )}
              </>
            )}
            {/* Conversations */}
            {activeTab === AIAnalyticsType.Conversations && data && (
              <>
                {(data as GetConversationResponse[]).length === 0 ? (
                  <NoData
                    height={150}
                    hint="Oops! No conversations found. Try tweaking the filters!"
                  />
                ) : (
                  (data as GetConversationResponse[]).map((item) => (
                    <ChatCard key={item.uuid} data={item} isFeedback={false} />
                  ))
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AIAnalytics;
